import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import {
  selectAnnotationsForPage,
  setAnnotationsForPage,
} from '../../../../../../../reducers/v2/annotationsV2/annotationsV2.slice';
import type { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import {
  ActiveTool,
  Annotation,
  AnnotationType,
  DocumentRotation,
  DragMode,
  ImageOriginalDimensions,
  Viewport,
} from '../types';
import {
  DEFAULT_STROKE_COLOR,
  DEFAULT_STROKE_WIDTH,
  ERASER_TOOL,
  POINTER_TOOL,
} from '../constants';
import { fromOriginalCoordinates, toOriginalCoordinates } from '@v2/components/imageHandling/components/DrawingCanvas/helpers';
import { useAnnotationUndoRedo } from './useAnnotationUndoRedo';

export interface UseManageDrawingCanvasParams {
  image: ImageDocumentStoreItem;
  pageNumber: number;
  viewport: Viewport;
  documentRotation: DocumentRotation;
  currentUserId: number;
  onExit: () => void;
}

export const useManageDrawingCanvas = ({
  image,
  pageNumber,
  viewport,
  documentRotation,
  currentUserId,
  onExit,
}: UseManageDrawingCanvasParams) => {
  const dispatch = useAppDispatch();

  const imageDimensions = useMemo<ImageOriginalDimensions>(
    () => ({ widthOriginal: image.widthOriginal, heightOriginal: image.heightOriginal }),
    [image.widthOriginal, image.heightOriginal]
  );

  const selectForPage = useMemo(
    () => selectAnnotationsForPage(image.documentId, pageNumber),
    [image.documentId, pageNumber]
  );
  const storedAnnotations = useSelector(selectForPage);

  // Working-space copy. Recomputed whenever the persisted slice entry changes or viewport/image resize.
  const [annotations, setAnnotations] = useState<Annotation[]>(() =>
    storedAnnotations.map((annotation) =>
      fromOriginalCoordinates({ annotation, image: imageDimensions, viewport, documentRotation })
    )
  );

  useEffect(() => {
    setAnnotations(
      storedAnnotations.map((annotation) =>
        fromOriginalCoordinates({ annotation, image: imageDimensions, viewport, documentRotation })
      )
    );
  }, [storedAnnotations, imageDimensions, viewport, documentRotation]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTool, setActiveTool] = useState<ActiveTool>(POINTER_TOOL);
  const [strokeColor, setStrokeColor] = useState<string>(DEFAULT_STROKE_COLOR);
  const [strokeWidth, setStrokeWidth] = useState<number>(DEFAULT_STROKE_WIDTH);
  const [fillActive, setFillActive] = useState<boolean>(false);
  const [dragMode, setDragMode] = useState<DragMode>(DragMode.None);

  const { undo, redo, pushUndoSnapshot, canUndo, canRedo } = useAnnotationUndoRedo({ annotations, setAnnotations });

  const deleteAnnotation = useCallback(
    (annotationId: string) => {
      pushUndoSnapshot();
      setAnnotations((prev) => prev.filter((a) => a.id !== annotationId));
      setSelectedId((current) => (current === annotationId ? null : current));
    },
    [pushUndoSnapshot]
  );

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    deleteAnnotation(selectedId);
  }, [selectedId, deleteAnnotation]);

  const commit = useCallback(() => {
    const original = annotations.map((annotation) =>
      toOriginalCoordinates({ annotation, image: imageDimensions, viewport, documentRotation })
    );
    dispatch(
      setAnnotationsForPage({
        documentId: image.documentId,
        pageNumber,
        annotations: original,
      })
    );
  }, [annotations, dispatch, image.documentId, pageNumber, imageDimensions, viewport, documentRotation]);

  const handleDone = useCallback(() => {
    commit();
    onExit();
  }, [commit, onExit]);

  const handleCancel = useCallback(() => {
    onExit();
  }, [onExit]);

  const replaceAnnotationStyle = useCallback(
    (annotationId: string, styleUpdate: Partial<Annotation['style']>) => {
      pushUndoSnapshot();
      setAnnotations((prev) =>
        prev.map((a) => (a.id === annotationId ? { ...a, style: { ...a.style, ...styleUpdate } } : a))
      );
    },
    [pushUndoSnapshot]
  );

  const handlePickColor = useCallback(
    (color: string) => {
      setStrokeColor(color);
      if (selectedId) replaceAnnotationStyle(selectedId, { strokeColor: color });
    },
    [selectedId, replaceAnnotationStyle]
  );

  const handlePickWidth = useCallback(
    (width: number) => {
      setStrokeWidth(width);
      if (selectedId) replaceAnnotationStyle(selectedId, { strokeWidth: width });
    },
    [selectedId, replaceAnnotationStyle]
  );

  const handleToggleFill = useCallback(
    (filled: boolean) => {
      setFillActive(filled);
      if (!selectedId) return;
      pushUndoSnapshot();
      setAnnotations((prev) =>
        prev.map((a) => {
          if (a.id !== selectedId) return a;
          if (a.type === AnnotationType.Rectangle || a.type === AnnotationType.Circle) {
            return { ...a, filled };
          }
          return a;
        })
      );
    },
    [selectedId, pushUndoSnapshot]
  );

  const isEraserActive = activeTool === ERASER_TOOL;

  return {
    // state
    annotations,
    selectedId,
    activeTool,
    strokeColor,
    strokeWidth,
    fillActive,
    dragMode,
    canUndo,
    canRedo,
    isEraserActive,
    imageDimensions,
    currentUserId,
    documentId: image.documentId,
    pageNumber,
    // setters / actions
    setAnnotations,
    setSelectedId,
    setActiveTool,
    setDragMode,
    pushUndoSnapshot,
    undo,
    redo,
    deleteAnnotation,
    deleteSelected,
    handleDone,
    handleCancel,
    handlePickColor,
    handlePickWidth,
    handleToggleFill,
  };
};

export type ManageDrawingCanvasApi = ReturnType<typeof useManageDrawingCanvas>;
